// Where chats live in the app: the Chats page (pages/app/ChatsPage.jsx) is the one history of
// every chat, /app/chats/<id> one chat in it and /app/chats/new a new one. Older links (a
// bot's page, /app/bots/<bot>?project=&chat=) still come in from notifications, phones and
// the desktop app: legacyChatTarget says where each one lands now.

/** One chat, in the Chats page. */
export const chatPath = (chatId) => `/app/chats/${chatId}`;

/** A new chat: with `botId` (else the default bot, components/bots/defaultBot.js) in `projectId`. */
export const newChatPath = ({ botId, projectId } = {}) => {
  const query = new URLSearchParams({ ...(botId ? { bot: botId } : {}), ...(projectId ? { project: projectId } : {}) });
  return `/app/chats/new${query.toString() ? `?${query}` : ''}`;
};

/** The history's filters in a link: ?bot=, ?project= (an id or 'none': chats in no project) and
 * ?needs_you=1 (only the chats that wait for the person). Read back by chatFilters. */
export const filterQuery = ({ botId, projectId, needsYou } = {}) => new URLSearchParams({
  ...(botId ? { bot: botId } : {}), ...(projectId ? { project: projectId } : {}), ...(needsYou ? { needs_you: '1' } : {}),
}).toString();

/** The filters a link to the history names (filterQuery). */
export const chatFilters = (search) => {
  const at = new URLSearchParams(search);
  return { botId: at.get('bot') || null, projectId: at.get('project') || null, needsYou: at.get('needs_you') === '1' };
};

/** The history, filtered (filterQuery). */
export const chatsPath = (filters = {}) => {
  const query = filterQuery(filters);
  return `/app/chats${query ? `?${query}` : ''}`;
};

/** The chat a link opens (/app/chats/<id>, or an older /app/bots/<bot>?chat=<id>), else null. */
export const chatIdFromUrl = (url) => {
  try {
    const at = new URL(url, 'https://app.local');
    const own = at.pathname.match(/^\/app\/chats\/([^/]+)$/);
    if (own) return own[1] === 'new' ? null : own[1];
    if (/^\/app\/bots\/[^/]+$/.test(at.pathname)) {
      const chat = at.searchParams.get('chat');
      return chat && chat !== 'new' && chat !== 'last' ? chat : null;
    }
  } catch { /* not a link */ }
  return null;
};

/** Where an older link to a bot's chat history lands (/app/bots/<botId>/chats): the history of
 * that bot (BusyBee's: the whole history, she has no id before she is made). */
export const legacyHistoryTarget = (botId) => chatsPath({ botId: botId === 'queen' ? null : botId });

/**
 * Where an older link to a bot's page lands: /app/bots/<botId> with ?project= and ?chat=.
 * A chat id opens that chat; 'new' (or none) a new chat with that bot, in the project when
 * one is named (BusyBee's chats are in no project). 'last' and a project without a chat want
 * the latest chat: `chats` (the bot's, newest first) decides once they are loaded.
 * Returns null while that is not known yet.
 */
export const legacyChatTarget = ({ botId, project, chat }, chats) => {
  if (chat && chat !== 'new' && chat !== 'last') return chatPath(chat);
  const queen = botId === 'queen';
  const newChat = newChatPath({ botId: queen ? null : botId, projectId: queen ? null : project });
  if (chat === 'new' || queen || (!project && chat !== 'last')) return newChat;
  if (!chats) return null;
  const latest = chat === 'last' ? chats[0] : chats.find((c) => c.project_id === project);
  return latest ? chatPath(latest.id) : newChat;
};
